import React from 'react';
import {
  TripVisibilityChoice,
  TripMemberDto,
  getTripMembers,
  addTripMember,
  removeTripMember,
  setTripVisibility,
} from '../../../services/trips/api/tripsApi';
import { getPublicProfile } from '../../../services/social/api';
import './ShareTripModal.css';

interface ShareTripModalProps {
  tripId: string;
  viewerRole: string;
  currentUserId?: string;
  /** Explicit stored visibility, when the owner overrode the account default. */
  visibility?: 'private' | 'followers' | 'public';
  effectiveVisibility?: 'private' | 'followers' | 'public';
  onClose: () => void;
  /** Owner changed visibility (so the page can refresh its chip). */
  onVisibilityChanged?: (choice: TripVisibilityChoice) => void;
  /** A member left the trip (page should navigate away / refresh). */
  onLeft?: () => void;
}

interface RosterEntry extends TripMemberDto {
  name?: string;
}

const VISIBILITY_OPTIONS: Array<{ value: TripVisibilityChoice; label: string; hint: string }> = [
  { value: 'default', label: 'Default', hint: 'Follows your account setting (public account → public, closed → followers)' },
  { value: 'private', label: 'Private', hint: 'Only you and the people on this trip' },
  { value: 'followers', label: 'Followers', hint: 'People who follow you' },
  { value: 'public', label: 'Public', hint: 'Anyone signed in' },
];

/**
 * Share modal (T2.3d): visibility picker (owner-only) + roster management.
 * Owner adds/removes collaborators & members; a member can leave.
 */
const ShareTripModal: React.FC<ShareTripModalProps> = ({
  tripId,
  viewerRole,
  currentUserId,
  visibility,
  effectiveVisibility,
  onClose,
  onVisibilityChanged,
  onLeft,
}) => {
  const isOwner = viewerRole === 'owner';
  const [choice, setChoice] = React.useState<TripVisibilityChoice>(visibility ?? 'default');
  const [savingVisibility, setSavingVisibility] = React.useState(false);
  const [roster, setRoster] = React.useState<RosterEntry[]>([]);
  const [rosterLoading, setRosterLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [newUserId, setNewUserId] = React.useState('');
  const [newRole, setNewRole] = React.useState<'member' | 'collaborator'>('member');
  const [busy, setBusy] = React.useState(false);

  const loadRoster = React.useCallback(async () => {
    setRosterLoading(true);
    try {
      const { members } = await getTripMembers(tripId);
      // Best-effort display names from public profiles.
      const enriched = await Promise.all(members.map(async (member) => {
        const profile = await getPublicProfile(member.userId).catch(() => null);
        const name = profile
          ? [profile.firstName, profile.lastName].filter(Boolean).join(' ') || profile.nickname || undefined
          : undefined;
        return { ...member, name: name || undefined };
      }));
      setRoster(enriched);
    } catch (err: any) {
      setError(err?.message || 'Failed to load the trip roster.');
    } finally {
      setRosterLoading(false);
    }
  }, [tripId]);

  React.useEffect(() => {
    void loadRoster();
  }, [loadRoster]);

  const handleVisibility = async (next: TripVisibilityChoice) => {
    if (!isOwner || savingVisibility) return;
    const previous = choice;
    setChoice(next);
    setSavingVisibility(true);
    setError(null);
    try {
      await setTripVisibility(tripId, next);
      onVisibilityChanged?.(next);
    } catch (err: any) {
      setChoice(previous);
      setError(err?.message || 'Failed to change visibility.');
    } finally {
      setSavingVisibility(false);
    }
  };

  const handleAdd = async () => {
    const target = newUserId.trim();
    if (!target || busy) return;
    setBusy(true);
    setError(null);
    try {
      await addTripMember(tripId, target, newRole);
      setNewUserId('');
      await loadRoster();
    } catch (err: any) {
      setError(err?.message || 'Failed to add this person.');
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async (userId: string) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await removeTripMember(tripId, userId);
      if (userId === currentUserId) {
        onLeft?.();
        return;
      }
      setRoster((current) => current.filter((entry) => entry.userId !== userId));
    } catch (err: any) {
      setError(err?.message || 'Failed to remove this person.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="v2-share-trip-modal" role="dialog" aria-modal="true" aria-labelledby="share-trip-title">
      <div className="v2-share-trip-modal__backdrop" onClick={onClose} />
      <section className="v2-share-trip-modal__panel">
        <header>
          <div>
            <p>Sharing</p>
            <h2 id="share-trip-title">Share trip</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Close share dialog">×</button>
        </header>

        {error && <p className="v2-share-trip-modal__error">{error}</p>}

        {isOwner && (
          <div className="v2-share-trip-modal__section" aria-label="Trip visibility">
            <h3>Who can see this trip</h3>
            {VISIBILITY_OPTIONS.map((option) => (
              <label key={option.value} className="v2-share-trip-modal__option">
                <input
                  type="radio"
                  name="trip-visibility"
                  checked={choice === option.value}
                  disabled={savingVisibility}
                  onChange={() => handleVisibility(option.value)}
                />
                <span>
                  <strong>
                    {option.label}
                    {option.value === 'default' && effectiveVisibility ? ` (${effectiveVisibility})` : ''}
                  </strong>
                  <em>{option.hint}</em>
                </span>
              </label>
            ))}
          </div>
        )}

        <div className="v2-share-trip-modal__section" aria-label="People on this trip">
          <h3>People</h3>
          {rosterLoading && <p className="v2-share-trip-modal__hint">Loading people…</p>}
          {!rosterLoading && roster.length === 0 && (
            <p className="v2-share-trip-modal__hint">Nobody else is on this trip yet.</p>
          )}
          {!rosterLoading && roster.map((entry) => (
            <div key={entry.userId} className="v2-share-trip-modal__person">
              <div>
                <strong>{entry.name || entry.userId}</strong>
                <span>{entry.role === 'collaborator' ? 'Collaborator — can edit' : 'Member — can view'}</span>
              </div>
              {(isOwner || entry.userId === currentUserId) && (
                <button type="button" disabled={busy} onClick={() => handleRemove(entry.userId)}>
                  {entry.userId === currentUserId ? 'Leave' : 'Remove'}
                </button>
              )}
            </div>
          ))}

          {isOwner && (
            <div className="v2-share-trip-modal__add">
              <input
                type="text"
                value={newUserId}
                placeholder="User ID to add"
                onChange={(event) => setNewUserId(event.target.value)}
              />
              <select value={newRole} onChange={(event) => setNewRole(event.target.value as 'member' | 'collaborator')}>
                <option value="member">Member (view)</option>
                <option value="collaborator">Collaborator (edit)</option>
              </select>
              <button type="button" disabled={busy || !newUserId.trim()} onClick={handleAdd}>Add</button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ShareTripModal;
